import { FC, Fragment } from 'react';

import { BodyText, Divider, Eyebrow, Stack } from '@servicetitan/design-system';

import { environment, SiteMode } from '../../api';
import {
    OpenApiSecuritySchemeApiKey,
    OpenApiSecuritySchemeOAuth2,
} from '../../api/documentation.api';
import * as Styles from './docs.module.scss';

interface SecurityApiKeyHeaderProps {
    scheme: OpenApiSecuritySchemeApiKey;
}

interface SecurityOAuthProps {
    scheme: OpenApiSecuritySchemeOAuth2;
    requiredScopes: string[];
}

export function getRequiredScopeText(
    requiredScopes: string[],
    scheme: OpenApiSecuritySchemeOAuth2,
) {
    return requiredScopes
        .map(scope => scheme.flows.clientCredentials.scopes[scope] ?? scope)
        .join(', ');
}

export const SecurityApiKeyHeader: FC<SecurityApiKeyHeaderProps> = ({ scheme }) => (
    <Stack className="m-t-4" direction="column" spacing={2}>
        <Divider alignContent="start">
            <Eyebrow className="m-r-half">{scheme.description}</Eyebrow>
        </Divider>
        <BodyText>
            This endpoint requires authentication via an API key.
            {environment.site.mode === SiteMode.TenantApis && (
                <Fragment>
                    {' Please refer to the '}
                    <a
                        className="Link Link--blue"
                        href="/docs/getting-started/register-an-app"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Register an App
                    </a>
                    {' guide for more information.'}
                </Fragment>
            )}
        </BodyText>
        <dl className={Styles.securitySchemeDetails}>
            <dt>Header name:</dt>
            <dd>
                <code>{scheme.name}</code>
            </dd>
            <dt>Header value:</dt>
            <dd>
                Application key (starts with “<code>ak1.</code>”)
            </dd>
        </dl>
    </Stack>
);

export const SecurityOAuth: FC<SecurityOAuthProps> = ({ scheme, requiredScopes }) => (
    <Stack className="m-t-4" direction="column" spacing={2}>
        <Divider alignContent="start">
            <Eyebrow className="m-r-half">{scheme.description}</Eyebrow>
        </Divider>
        <BodyText>
            This endpoint requires authentication and authorization via OAuth 2.0.
            {environment.site.mode === SiteMode.TenantApis && (
                <Fragment>
                    {' Please refer to the '}
                    <a
                        className="Link Link--blue"
                        href="/docs/getting-started/register-an-app"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Register an App
                    </a>
                    {', '}
                    <a
                        className="Link Link--blue"
                        href="/docs/getting-started/client-id-secret"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Client ID & Secret
                    </a>
                    {', and '}
                    <a
                        className="Link Link--blue"
                        href="/docs/getting-started/first-api-call"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        First API Call
                    </a>
                    {' guides for more information.'}
                </Fragment>
            )}
        </BodyText>
        <dl className={Styles.securitySchemeDetails}>
            <dt>Token URL:</dt>
            <dd>
                <code>{scheme.flows.clientCredentials.tokenUrl}</code>
            </dd>
            <dt>Required scope:</dt>
            <dd>{getRequiredScopeText(requiredScopes, scheme)}</dd>
        </dl>
    </Stack>
);
